import React, {CSSProperties, useMemo, useRef} from 'react'
import styled from 'styled-components'
import {
  GIFT_SHOP_RESEND_VIDEO_MAX_DURATION,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {useDebounce, useLocation} from 'utils'
import {useWindowLayout} from 'windows'
import {Button, ButtonOutline} from 'common/components'
import convertUnit from 'lib/unit'
import {GiftShopResendRevisionContentVideoProps} from './GiftShopResendRevisionContentProps'
import {
  GiftShopResendRevisionImageItemVideoExisting,
  GiftShopResendRevisionImageItemVideoRevised,
} from '../ImageItem'

const StyledOuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0 ${convertUnit(20)};
  flex-wrap: wrap;
  overflow-y: scroll;
  justify-content: space-between;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    padding: 0;
    gap: 0;
  }
`

const StyledContentContainer = styled.div<CSSProperties>`
  width: 100%;
  max-width: 32%;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: 100%;
    max-width: 49%;
  }
`

const StyledPanthom = styled.div`
  height: ${convertUnit(70)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    height: ${convertUnit(85)};
  }
`

const StyledFooter = styled.div`
  width: 100%;
  position: absolute;
  bottom: 0;
`

const StyledButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${convertUnit(12)} ${convertUnit(20)};
  align-items: center;
  position: sticky;
  gap: ${convertUnit(20)};
  background-color: ${({theme}) => theme.white_1};
`

const StyledButton = styled(Button)`
  width: 100%;
`

const StyledButtonOutline = styled(ButtonOutline)`
  width: 100%;
`

export default function GiftShopResendRevisionContentVideo({
  revisionData,
  revisionFile,
  loadingBtn,
  onAddPhoto,
  onUpload,
  onVideoLoaded,
}: GiftShopResendRevisionContentVideoProps) {
  const {translate} = useTranslation()
  const {
    hiresUrl,
    originalAt,
    originalUrl,
    title,
    contentType,
    originalDuration,
    hiresDuration,
    originalSize,
    hiresSize,
  } = useLocation('giftshop_resend_revision').state
  const videoReviseRef = useRef<HTMLVideoElement>(null)
  const {width} = useWindowLayout()

  const debounce = useDebounce()

  const handleRenderStandardVersion = useMemo(
    () => (
      <StyledContentContainer maxWidth={(width - 4) / 2}>
        <GiftShopResendRevisionImageItemVideoExisting
          versionTitle={translate('giftShop:notHires')}
          date={originalAt.toString()}
          src={originalUrl}
          title={title}
          duration={originalDuration}
          size={originalSize}
        />
      </StyledContentContainer>
    ),
    [
      originalAt,
      originalDuration,
      originalSize,
      originalUrl,
      title,
      translate,
      width,
    ],
  )

  const handleRenderCurrentHiresVersion = useMemo(
    () => (
      <StyledContentContainer maxWidth={(width - 4) / 2}>
        <GiftShopResendRevisionImageItemVideoExisting
          versionTitle={`${translate('global:hires')} (${translate(
            'global:current',
          )})`}
          date={originalAt.toString()}
          src={hiresUrl}
          title={title}
          duration={
            hiresDuration &&
            hiresDuration > GIFT_SHOP_RESEND_VIDEO_MAX_DURATION * 1000
              ? GIFT_SHOP_RESEND_VIDEO_MAX_DURATION * 1000
              : hiresDuration
          }
          size={hiresSize}
        />
      </StyledContentContainer>
    ),
    [hiresDuration, hiresSize, hiresUrl, originalAt, title, translate, width],
  )

  const handleRenderRevisionHiresVersion = useMemo(
    () => (
      <StyledContentContainer maxWidth={(width - 4) / 2}>
        <GiftShopResendRevisionImageItemVideoRevised
          onVideoLoaded={onVideoLoaded}
          versionTitle={`${translate('global:hires')} (${translate(
            'global:revision',
          )})`}
          videoRef={videoReviseRef}
          resendFile={revisionFile}
          resendData={revisionData}
        />
      </StyledContentContainer>
    ),
    [onVideoLoaded, revisionData, revisionFile, translate, width],
  )

  const handleButtonLabel = useMemo(() => {
    if (revisionData) {
      return translate(
        contentType === 'photo'
          ? 'giftShop:changePhotoButton'
          : 'giftShop:videoResendChangeButton',
      )
    }
    return translate(
      contentType === 'photo'
        ? 'giftShop:addPhoto'
        : 'giftShop:videoResendAddButton',
    )
  }, [contentType, revisionData, translate])

  const handleRenderButtons = useMemo(
    () => (
      <StyledFooter>
        <StyledButtonsContainer>
          <StyledButtonOutline
            borderColor="gray_5"
            color="gray_5"
            label={handleButtonLabel}
            backgroundHoverColor="white_1"
            onClick={loadingBtn ? undefined : onAddPhoto}
          />
          <StyledButton
            disabled={!revisionData}
            onClick={() =>
              loadingBtn ? undefined : debounce(() => onUpload(), 300)
            }
            isLoading={loadingBtn}
            label={translate('global:upload')}
          />
        </StyledButtonsContainer>
      </StyledFooter>
    ),
    [
      debounce,
      handleButtonLabel,
      loadingBtn,
      onAddPhoto,
      onUpload,
      revisionData,
      translate,
    ],
  )

  return (
    <StyledOuterContainer>
      <StyledContainer>
        {handleRenderStandardVersion}
        {handleRenderCurrentHiresVersion}
        {handleRenderRevisionHiresVersion}
      </StyledContainer>
      <StyledPanthom />
      {handleRenderButtons}
    </StyledOuterContainer>
  )
}
